import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import { parsePhoneNumberFromString, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';

import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import showToast from "../../utils/toastNotifications";
import { COUNTRY_CODES } from "../../constants/commonConstants";
import { FORM_MODES, firebaseUplaodFolders, maximumImagesUploadingSizes } from "../../constants/commonConstants";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";

import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButton";
import FileUploadInput from "../../components/Form/FileUploadInput";
import DropDownNew from "../../components/Form/Dropdown";
import ConfirmationModal from "../../components/Model/ConfirmationModel";
import {
  addTenantData,
  editTenantData,
  fetchUnitData,
  deleteTenant,
  deleteTenantWithReview,
  fetchPrevRatings
} from "../../store";
import RateReviewModal from "../../components/Model/RateReviewModal";
import PreviousRatingsModal from "../../components/Model/PreviousRatingsModal";

const formatInternationalPhoneNumber = (input) => {
  // Remove any existing formatting first
  let cleanInput = input.replace(/[^\d+]/g, '');

  // If input doesn't start with +, and has digits, assume it needs country code
  if (cleanInput && !cleanInput.startsWith('+')) {
    cleanInput = '+' + cleanInput;
  }

  // Use AsYouType for real-time formatting
  const formatter = new AsYouType();
  const formatted = formatter.input(cleanInput);
  // console.log("formatter", formatted);

  return formatted;
};

const validateInternationalPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim().length < 0) {
    return "Phone number is required";
  }

  if (!isValidPhoneNumber(phoneNumber)) {
    return "Please enter a valid phone number";
  }

  try {
    const parsed = parsePhoneNumberFromString(phoneNumber);

    if (!parsed) {
      return "Please enter a valid phone number with country code";
    }

    if (!parsed.isValid()) {
      return "Please enter a valid phone number";
    }

    return null; // Valid
  } catch (error) {
    return "Please enter a valid international phone number";
  }
};

const notificationOptions = [
  { label: "Email", value: "Email" },
];
const notificationOptionsWithSMS = [
  { label: "Email", value: "Email" },
  { label: "SMS", value: "SMS" },
];

const Tenantdetailsform = ({ mode, unitId, propertyId }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [tenantData, setTenantData] = useState();
  const [selectedProperty, setSelectedProperty] = useState();
  const [selectedUnit, setSelectedUnit] = useState();
  const [requiredEmail, setRequiredEmail] = useState(true);
  const [phoneNumberError, setPhoneNumberError] = useState("");

  // 1) First confirm (“Are you sure?”)
  const [isFirstConfirmOpen, setIsFirstConfirmOpen] = useState(false);
  // 2) Leave a review? (Yes/No)
  const [isReviewPromptOpen, setIsReviewPromptOpen] = useState(false);
  // 3) Rate & Review modal
  const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  // previous ratings modal state
  const [isPrevRatingsOpen, setIsPrevRatingsOpen] = useState(false);
  const [prevRatings, setPrevRatings] = useState([]);
  const [isLoadingPrevRatings, setIsLoadingPrevRatings] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState({
    type: "delete",
    title: "Are you sure?",
    message: "Are you sure you want to remove this tenant? This action is irreversible.",
    confirmText: "DELETE",
    cancelText: "CANCEL"
  });
  const [entryDateError, setEntryDateError] = useState(""); // calender only today .. +3 months

  const navigate = useNavigate();

  const properties = useSelector((state) => state.property.properties);

  const [doFetchUnitData, isFetchingUnitData, fetchUnitDataError] = useThunk(fetchUnitData);
  const [doEditTenant, isEditingTenant, editTenantError] = useThunk(editTenantData);
  const [doAddTenant, isAddingTenant, addTenantError] = useThunk(addTenantData);
  const [doFetchPrevRatings, isFetchPrevRatings] = useThunk(fetchPrevRatings);
  const [doDeleteTenant, isDeletingTenant, deleteTenantError] = useThunk(deleteTenant);
  const [doDeleteTenantWithReview, isDeleteTenantWithReview] = useThunk(deleteTenantWithReview)
  const [doUploadRefrencesToFirebase, isUploadRefrencesToFirebaseLoading, uploadRefrencesToFirebaseError] = useFirebaseFileUpload();
  const [ratingsOpened, setRatingsOpened] = useState(false);
  const MAX_ADDRESS_LENGTH = 250;

  useEffect(() => {
    const fetchUnitData = async () => {
      const property = properties.find(property => property._id === propertyId);

      if (property) {
        setSelectedProperty(property);
      }

      const result = await doFetchUnitData({ unitId: unitId });
      if (result.success) {
        setTenantData(result?.response?.data?.tenant);
        setSelectedUnit(result?.response?.data);

        const tenant = result?.response?.data?.tenant;

        // Safe date conversion function
        const formatDateForInput = (dateValue) => {
          if (!dateValue) return "";
          const date = new Date(dateValue);
          // Check if the date is valid
          if (isNaN(date.getTime())) {
            return "";
          }
          return toISODateInput(date);
        };

        setFormData({
          tenantName: tenant?.name || "",
          email: tenant?.email || "",
          phone: tenant?.phoneNumber || "",
          address: tenant?.address || "",
          notification: {
            value: tenant?.notificationPreferences || "Email",
            label: tenant?.notificationPreferences || "Email",
          },
          comments: tenant?.comments || "",
          documents: tenant?.documents || [],
          entryDate: formatDateForInput(result?.response?.data?.tenantStartDate),
        });
      }
    }

    fetchUnitData();
  // }, [doFetchUnitData, mode, unitId, properties, propertyId]);
  }, [doFetchUnitData, mode, unitId]);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
    setFormData
  } = useForm(
    {
      tenantName: "",
      email: "",
      phone: "",
      address: "",
      notification: { value: "Email", label: "Email" },
      comments: "",
      documents: null,
      entryDate: "",
    },
    {
      tenantName: true,
      email: requiredEmail,
      phone: true,
      address: true,
      notification: true,
      comments: false,
      documents: false,
      entryDate: true,
    },
    {
      tenantName: COMMON_FIELD_TYPES.FULLNAME,
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      phone: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      address: COMMON_FIELD_TYPES.ADDRESS,
      notification: COMMON_FIELD_TYPES.DROPDOWN,
      comments: COMMON_FIELD_TYPES.TEXT_FIELD,
      documents: COMMON_FIELD_TYPES.TEXT_FIELD,
      entryDate: COMMON_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  useEffect(() => {
    if (
        !ratingsOpened &&
        formData?.email?.trim() !== "" &&
        formData?.phone?.trim() !== ""
    ) {
      setRatingsOpened(true);
    }
  }, [formData.email, formData.phone, ratingsOpened]);


  // Open step 1 (Are you sure?)
  const handleShowDeleteModal = () => setIsFirstConfirmOpen(true);

  // Step 1 CANCEL
  const handleCancelFirstConfirm = () => setIsFirstConfirmOpen(false);

  // Step 1 DELETE → go to Step 2 ("Leave a review?")
  const handleProceedToReviewPrompt = () => {
    setIsFirstConfirmOpen(false);
    setIsReviewPromptOpen(true);
  };

  // Step 2 "No" → delete immediately
  const handleDeleteWithoutReview = async () => {
    setIsReviewPromptOpen(false);
    await actuallyDeleteTenant(); // common deleter
  };

  // Step 2 "Yes" → open review form (Step 3)
  const handleOpenReviewForm = () => {
    setIsReviewPromptOpen(false);
    setIsReviewFormOpen(true);
  };

  // Common delete
  const actuallyDeleteTenant = async (reviewPayload = null) => {
    const payload = { propertyId, unitId, tenantId: tenantData?._id };
    if (reviewPayload) payload.review = reviewPayload; // backend can use/ignore

    const result = await doDeleteTenant(payload);
    if (result?.success) {
      showToast('success', 'Tenant deleted successfully.');
      navigate(`/landlords/manage-property/view-details/${propertyId}`);
    } else {
      showToast('error', 'Failed to delete tenant' || result?.error);
      // console.error("Delete tenant error:", result?.error);
    }
  };

  // Step 3 submit review → delete
  const handleSubmitReviewAndDelete = async () => {
    if (!reviewRating || reviewRating < 1 || reviewRating > 5) {
      showToast("error", "Please select a rating between 1 and 5.");
      return;
    }
    setIsSubmittingReview(true);

    const reviewPayload = {
      rating: reviewRating,
      comment: reviewComment?.trim() || "",
      tenantId: tenantData?._id,
      propertyId,
      unitId,
      createdAt: new Date().toISOString()
    };

    setIsReviewFormOpen(false);
    const result = await doDeleteTenantWithReview({
      propertyId,
      unitId,
      tenantId: tenantData?._id,
      review: reviewPayload
    });
    if (result?.success) {
      showToast('success', 'Tenant deleted successfully.');
      navigate(`/landlords/manage-property/view-details/${propertyId}`);
    } else {
      showToast('error', result?.error ? result?.error.message :'Failed to delete tenant' );
      // console.error("Delete tenant error:", result?.error);
    }

    setIsSubmittingReview(false);
    setReviewRating(5);
    setReviewComment("");
  };

  const validateAddress = (addr) => {
  if (!addr || !addr.trim()) return "Tenant address is required";
  if (addr.trim().length > MAX_ADDRESS_LENGTH) {
    return `Address must not exceed ${MAX_ADDRESS_LENGTH} characters`;
  }
  return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // handleBlur("tenantName", formData.tenantName);
    // handleBlur("phone", formData.phone);
    // handleBlur("address", formData.address);

    setTouched({
      tenantName: true,
      email: true,
      phone: true,
      address: true,
      notification: true,
      comments: true,
      countryCode: true,
      documents: true,
      entryDate: true,  // calender only today .. +3 months
    });

    const isValid = validateForm();

    const tenantNameError = validateTenantName(formData.tenantName);
    if (tenantNameError) {
      showToast("error", tenantNameError);
      return;
    }

    const addressError = validateAddress(formData.address);
    if (addressError) {
      showToast("error", addressError);
      return;
    }

    if (phoneNumberError) {
      showToast("error", phoneNumberError);
      return;
    }

    if (!isValid) {
      showToast("error", "Please fill in all required fields");
      return;
    }

    // Handle file uploads
    const uploadFilesResponse = await doUploadRefrencesToFirebase(
      uploadedFiles,
      firebaseUplaodFolders.F_TENANT,
      maximumImagesUploadingSizes.size_1MB
    );

    if (!uploadFilesResponse.success) {
      showToast("error", "Failed to upload files. Please try again." || uploadFilesResponse.error);
      // console.error("File upload error:", uploadFilesResponse.error);
      return;
    }

    const uploadedFilesData = uploadFilesResponse.uploadedUrls;
    // console.log('uploadedFilesData', uploadedFilesData);

    if (mode === FORM_MODES.EDIT) {
      const documentList = [...formData.documents, ...uploadedFilesData];
      // console.log('documentList', documentList);
      // Transformed object 
      const transformedData = {
        name: formData.tenantName?.trim(),
        phoneNumber: formData.phone?.trim(),
        email: formData.email?.trim(),
        address: formData.address?.trim(),
        notificationPreferences: formData.notification?.value?.toUpperCase(),
        comments: formData.comments?.trim(),
        documents: documentList.length > 0 ? documentList : null,
        whenVacant: formData.entryDate,
      };
      // console.log('transformedData', transformedData);
      const result = await doEditTenant({ propertyId: propertyId, unitId: unitId, tenantId: tenantData?._id, data: transformedData });

      if (result?.success) {
        showToast('success', 'Tenant updated successfully.');
        navigate(`/landlords/manage-property/view-details/${propertyId}`);
      } else {
        showToast('error', 'Failed to update unit' || result?.error);
      }
    }

    if (mode === FORM_MODES.ADD) {
      // console.log('Hit')
      // Transformed object 
      const transformedData = {
        name: formData.tenantName?.trim(),
        phoneNumber: formData.phone?.trim(),
        email: formData.email?.trim(),
        address: formData.address?.trim(),
        notificationPreferences: formData.notification?.value?.toUpperCase(),
        comments: formData.comments?.trim(),
        whenVacant: formData.entryDate || null, // calender only today .. +3 months
        documents: uploadedFilesData.length > 0 ? uploadedFilesData : null,
        whenVacant: formData.entryDate,
      };

      const result = await doAddTenant({ propertyId: propertyId, unitId: unitId, data: transformedData });

      if (result.success) {
        showToast('success', 'Unit updated successfully.');
        navigate(`/landlords/manage-property/view-details/${propertyId}`);
      }
    }
  };

  const handleCancel = () => {
    // Reset form data and uploaded files
    setFormData({
      tenantName: "",
      email: "",
      phone: "",
      address: "",
      notification: { value: "Email", label: "Email" },
      comments: "",
      documents: null,
      countryCode: COUNTRY_CODES[0].code, // Default to first country code
      entryDate: "", // calender only today .. +3 months
    });
    setUploadedFiles([]);
    navigate(`/landlords/manage-property/view-details/${propertyId}`);
  }

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Clear the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index, isExisting = false) => {
    if (isExisting) {
      setFormData(prev => ({
        ...prev,
        documents: prev.documents.filter((_, i) => i !== index)
      }));
    } else {
      setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleValidationError = (invalidFiles) => {
    showToast("error", "Some files are invalid. Please check the file types and sizes.");
  };

  // console.log('formData', formData);
  
      // --- Date helpers (min=today, max=today+3 months) --- // calender only today .. +3 months
  const toISODateInput = (d) => d.toISOString().slice(0,10); // YYYY-MM-DD

  const getTodayISO = () => {
    const d = new Date();
    // normalize to local date (strip time)
    d.setHours(0,0,0,0);
    return toISODateInput(d);
  };

  const getMax3MonthsISO = () => {
    const d = new Date();
    d.setHours(0,0,0,0);
    d.setMonth(d.getMonth() + 3);
    return toISODateInput(d);
  };

  const isWithinNext3Months = (val) => {
    if (!val) return false;
    const chosen = new Date(val + 'T00:00:00');
    const min = new Date(getTodayISO() + 'T00:00:00');
    const max = new Date(getMax3MonthsISO() + 'T00:00:00');
    return chosen >= min && chosen <= max;
  };
  const validateTenantName = (name) => {
    const value = (name || "").trim();

    if (!value) return "Tenant name is required";
    if (value.length > 100) return `Only ${100} characters are allowed.`;

    const regex = /^[A-Za-z\s]+$/;
    if (!regex.test(value)) 
      return "Only letters and spaces are allowed.";
    return null;
  };
  const openPreviousRatings = async () => {
    const email = (formData.email || "").trim();
    const phoneNumber = formData.phone?.trim()
       if (!email) {
           showToast("error", "Please enter an email first.");
           return;
       }
        if (!phoneNumber) {
          showToast("error", "Please enter a phone number first.");
          return;
        }
       setIsLoadingPrevRatings(true);
       setPrevRatings([]);
       const res = await doFetchPrevRatings({ email, phoneNumber });
       setIsLoadingPrevRatings(false);

       if (res.success === true) {
         setPrevRatings(res.response.data);
         setIsPrevRatingsOpen(true);
       }else{
         showToast("error", res?.error.message ? res?.error.message : "Unable to fetch ratings");
       }
  };
  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <header className="flex justify-between items-center bg-white p-4 rounded-lg shadow-md h-[87px]">
        <div className="flex items-center space-x-2">
          <span className="text-xs font-semibold text-blue-600 sm:text-sm md:text-base lg:text-lg">
            {selectedProperty?.propertyName}
          </span>
          <span className="text-[#113092] font-bold text-xs sm:text-sm md:text-xl lg:text-2xl">
            {selectedUnit?.unitId}
          </span>
          <span className="text-[#113092] font-bold text-xs sm:text-sm md:text-lg lg:text-xl">
            {selectedUnit?.unitName && selectedUnit?.unitName !== "-" ? selectedUnit?.unitName : ""}
          </span>
        </div>
        {mode === FORM_MODES.EDIT && (
          <CustomButton
            buttonText="Remove Tenant"
            bgColor="bg-white"
            textColor="text-red-500"
            borderColor="border-red-500"
            textWeight="font-normal"
            className="
                px-2 sm:px-0 md:px-0 py-1 sm:py-1.5 md:py-2
                h-[26px] sm:h-[34px] md:h-[42px] lg:h-[45px]
                w-[100px] sm:w-[120px] md:w-[142px] lg:w-[155px] min-w-[100px]
                rounded-[8px]
                hover:bg-red-200 transition
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            // className="border border-red-500 text-red-500 font-normal px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-[8px] hover:bg-red-100 transition text-xs sm:text-sm md:text-base lg:text-lg"
            onClick={handleShowDeleteModal}
            loading={isDeletingTenant}
            disabled={isDeletingTenant}
          />
        )}
      </header>

      <div className="w-full p-6 mx-auto mt-10 bg-white shadow-md rounded-xl sm:p-8 ">
        <div
          className="flex flex-col gap-y-4 sm:gap-y-6"
        // onSubmit={handleSubmit}
        >
          {/* Tenant Name */}
          <CustomInput
            label={
              <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                Tenant Name <span className="text-light-red">*</span>
              </span>
            }
            type="text"
            name="tenantName"
            value={formData.tenantName}
            placeholder="Enter tenant name"
            error={
              touched.tenantName &&
              (errors.tenantName || validateTenantName(formData.tenantName))
            }
            onChange={(e) => handleInputChange("tenantName", e.target.value)}
            onBlur={() => {
              handleBlur("tenantName", formData.tenantName);
              const customError = validateTenantName(formData.tenantName);
              if (customError) {
                errors.tenantName = customError;
              }
            }}
            onFocus={() => clearError("tenantName")}
            labelStyle="text-[#09114B] text-lg font-inter font-medium"
            inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
            className="
              inline-flex items-center flex-shrink-0 
              border border-gray-300 
              bg-primary-light/30 text-content 
              placeholder:text-[#9D9D9D]
              h-8 sm:h-9 md:h-10 lg:h-[45px] rounded-[5px] px-[13px] py-[14px]
              w-full min-w-[120px]
            "
          />

          {/* Email and Phone */}
          <div className="flex flex-col md:flex-row gap-y-4 md:gap-6">
            <CustomInput
              label={
                <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                  Email Address
                </span>
              }
              isRequired={requiredEmail}
              type="email"
              name="email"
              value={formData.email}
              placeholder="Enter email address"
              error={touched.email && errors.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => handleBlur("email", formData.email)}
              onFocus={() => clearError("email")}
              labelStyle="text-[#09114B] text-lg font-inter font-medium"
              inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
              className="
                inline-flex items-center flex-shrink-0 
                text-content w-full min-w-[120px]
                bg-primary-light/30 
                placeholder:text-[#9D9D9D]
                h-8 sm:h-9 md:h-10 lg:h-[45px] 
                py-[6px] px-[13px]
                rounded-[5px] 
                border border-[rgba(129,129,129,0.5)]
              "
            />

            {/* Phone with right-aligned "Previous Ratings on Tenafix" */}
            <div className="w-full min-w-[120px]">
                <div className="flex items-center justify-between">
                  <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                    Phone Number <span className="text-light-red">*</span>
                  </span>
                  { ratingsOpened &&
                    <button
                        type="button"
                        onClick={openPreviousRatings}
                        className="
                         text-[8px] sm:text-[9px] md:text-[10px] lg:text-[11px] xl:text-[12px]
                          text-[#113092] hover:text-[#0b1f6f]
                          underline underline-offset-2 decoration-[#113092]/50
                          font-medium
                        "
                    >
                      Previous Ratings on Tenafix
                    </button>
                  }
                </div>

              <div className="flex items-center w-full gap-2 mt-1 sm:mt-2">
                <CustomInput
                    type="tel"
                    name="phone"
                    placeholder="Phone number"
                    value={formData.phone}
                    error={touched.phone && phoneNumberError}
                    onChange={(e) => {
                      handleInputChange("phone", e.target.value);
                      setPhoneNumberError(validateInternationalPhoneNumber(e.target.value));
                    }}
                    onBlur={() => {
                      const formatted = formatInternationalPhoneNumber(formData.phone);
                      handleInputChange("phone", formatted);
                      handleBlur("phone", formatted);
                      setPhoneNumberError(validateInternationalPhoneNumber(formatted));
                      if (!formatted?.startsWith('+256')){
                        setFormData(prev => ({
                          ...prev,
                          notification: { value: "Email", label: "Email" }
                        }));
                        setRequiredEmail(true);
                      }
                  }}
                  onFocus={() => clearError("phone")}
                  className={`
                    flex-grow w-full min-w-[100px]
                    h-8 sm:h-9 md:h-10 lg:h-[45px]
                    px-4 bg-white rounded-[5px] text-content focus:outline-none focus:ring-2 focus:ring-white
                    text-xs sm:text-sm md:text-base lg:text-lg
                    placeholder:text-[#9D9D9D]
                    border ${touched.phone && errors.phone
                      ? "border-red-500"
                      : "border-[#81818180]"
                    }
                `}
                />
              </div>
            </div>
          </div>

          {/* Tenant Address */}
          <CustomInput
            label={
              <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                Tenant Address <span className="text-light-red">*</span>
              </span>
            }
            type="text"
            name="address"
            value={formData.address}
            placeholder="Enter tenant address"
            error={
              touched.address &&
              (errors.address || validateAddress(formData.address))
            }
            onChange={(e) => handleInputChange("address", e.target.value)}
            onBlur={() => {
              handleBlur("address", formData.address);
              const customError = validateAddress(formData.address);
              if (customError) {
                errors.address = customError;
              }
            }}
            onFocus={() => clearError("address")}
            labelStyle="text-[#09114B] text-lg font-inter font-medium"
            className={`
              text-content bg-white
              placeholder:text-[#9D9D9D]
              w-full min-w-[120px]
              h-8 sm:h-9 md:h-10 lg:h-[45px]
              rounded-[5px]
              py-[6px] px-[13px]
              text-xs sm:text-sm md:text-base lg:text-lg
              ${touched.address && (errors.address || validateAddress(formData.address))
                ? "border border-red-400"
                : "border border-[rgba(129,129,129,0.5)]"
              }
            `}
          />

          {/* Notification Preference */}
          <div className="flex flex-col md:flex-row gap-y-4 md:gap-6">

            <DropDownNew
              label="Notification Preference"
              labelStyle="
                block mb-2
                text-[#09114B]
                text-xs sm:text-sm md:text-base lg:text-lg
                font-inter font-medium "
              name="notification"
              isRequired={true}
              error={touched.notification && errors.notification}
              options={formData.phone?.startsWith('+256') ? notificationOptionsWithSMS : notificationOptions}
              defaultOption={formData.notification}
              onSelect={(value) => {
                handleInputChange("notification", value);
                if (value.value === "Email") {
                  setRequiredEmail(true);
                } else {
                  setRequiredEmail(false);
                }
              }}
              placeholder="Select a preference"
              displayKey="label"
              idKey="value"
              buttonClassName={`
                inline-flex items-center flex-shrink-0
                bg-primary-light/30 text-content
                border-gray-300 rounded-[4px]
                h-8 sm:h-9 md:h-10 lg:h-[45px]
                px-[13px] w-full min-w-[120px]
                text-xs sm:text-sm md:text-base lg:text-lg
              `}
              dropdownClassName="
                w-full relative mt-1 rounded-[4px]
                bg-white border border-gray-300 shadow-md
                z-10 min-w-[120px]
              "
              optionClassName="
                block px-3 py-2 hover:bg-gray-100
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              selectedValueClassName="
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              placeholderClassName="
                text-[#9D9D9D]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            />
            {/* Entry Date (only today .. +3 months)   calender only today .. +3 months */}
            <div className="w-full min-w-[120px] mt-2">
              <span className="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                When is the tenant entering? <span className="text-light-red">*</span>
              </span>
              <input
                type="date"
                name="entryDate"
                value={formData.entryDate}
                min={getTodayISO()}
                max={getMax3MonthsISO()}
                onChange={(e) => {
                  setEntryDateError("");
                  handleInputChange("entryDate", e.target.value);
                }}
                onBlur={() => {
                  handleBlur("entryDate", formData.entryDate);
                  if (!formData.entryDate) {
                    setEntryDateError("Entry date is required");
                  } else if (!isWithinNext3Months(formData.entryDate)) {
                    setEntryDateError(`Select a date from ${getTodayISO()} to ${getMax3MonthsISO()}`);
                  } else {
                    setEntryDateError("");
                  }
                }}
                className={` cursor-pointer
                  mt-1 sm:mt-2 w-full
                  h-8 sm:h-9 md:h-10 lg:h-[45px]
                  px-4 bg-white rounded-[5px] text-content
                  text-xs sm:text-sm md:text-base lg:text-lg
                  placeholder:text-[#9D9D9D]
                  border ${ (touched.entryDate && (errors.entryDate || entryDateError))
                            ? "border-red-500"
                            : "border-[#81818180]"
                          }
                  focus:outline-none focus:ring-2 focus:ring-white
                `}
              />
              {(touched.entryDate && (errors.entryDate || entryDateError)) && (
                <p className="text-red-500 text-xs mt-1">
                  {entryDateError || "Entry date is required"}
                </p>
              )}
            </div>
          </div>

          {/* Comments */}
          <div>
            <CustomInput
              label="Comments (Optional)"
              type="textarea"
              name="comments"
              placeholder="Add internal notes or special instruction..."
              value={formData.comments}
              onChange={(e) => handleInputChange("comments", e.target.value)}
              onBlur={() => handleBlur("comments", formData.comments)}
              onFocus={() => clearError("comments")}
              labelStyle="text-[#09114B] text-xs sm:text-sm md:text-base lg:text-lg font-medium"
              inputStyle="text-xs sm:text-sm md:text-base lg:text-lg"
              className="
                placeholder:text-[#9D9D9D]
                border border-[rgba(129,129,129,0.5)]
                h-[80px] py-[6px] px-[13px]
                w-full min-w-[120px]
              "
              outerContainerStyle="mt-3"
            />
          </div>

          {/* File Upload */}
          <FileUploadInput
            fileInputRef={fileInputRef}
            onFileChange={handleFileChange}
            files={uploadedFiles}
            existingsFiles={formData.documents || []}
            onRemoveFile={handleRemoveFile}
            onValidationError={handleValidationError}
          />

          {/* Buttons */}
          <div className="flex flex-wrap justify-center gap-3 mt-6 sm:gap-5 sm:mt-8">
            {/* <button
              type="button"
              onClick={() => window.location.reload()}
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[120px] md:w-[120px] min-w-[120px]
                border border-[#4772F1]
                text-[#4772F1] font-bold rounded-[8px]
                hover:bg-[#f0f4ff]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[150px] md:w-[170px] min-w-[120px]
                bg-[#4772F1] text-white font-bold rounded-[8px]
                hover:bg-[#5c81ef]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
            >
              { mode === FORM_MODES.EDIT ? 'Update' : 'Submit'}
            </button> */}
            <CustomButton
              type="button"
              buttonText="Cancel"
              bgColor="bg-white"
              textColor="text-blue"
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[120px] md:w-[120px] min-w-[120px]
                border border-[#4772F1]
                text-[#4772F1] font-bold rounded-[8px]
                hover:bg-[#f0f4ff]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              // className=" w-full md:w-[220px] h-10 sm:h-14 text-[12px] sm:text-[16px] font-alexandria font-semibold rounded-md border border-blue"
              onClick={handleCancel}
              disabled={isAddingTenant || isEditingTenant}
            />
            <CustomButton
              type="button"
              buttonText={`${mode === FORM_MODES.EDIT ? 'Update' : 'Submit'}`}
              bgColor="bg-blue"
              textColor="text-white"
              className="
                h-[30px] sm:h-[45px] md:h-[55px]
                w-[120px] sm:w-[150px] md:w-[170px] min-w-[120px]
                bg-[#4772F1] text-white font-bold rounded-[8px]
                hover:bg-[#5c81ef]
                text-xs sm:text-sm md:text-base lg:text-lg
              "
              // className=" w-full md:w-[220px] h-10 sm:h-14 text-[12px] sm:text-[16px] font-alexandria font-semibold rounded-md"
              onClick={handleSubmit}
              loading={isAddingTenant || isEditingTenant || isUploadRefrencesToFirebaseLoading}
              disabled={isAddingTenant || isEditingTenant}
            />
          </div>
        </div>
      </div>
      {/* 1) First confirm: Are you sure? (Alert icon, CANCEL / DELETE) */}
      <ConfirmationModal
        isOpen={isFirstConfirmOpen}
        options={{
          type: "delete", // your modal likely shows alert icon when type="delete"
          title: "Delete Tenant",
          message: "Are you sure you want to delete this tenant? This will remove all associated units.",
          confirmText: "CONFIRM",
          cancelText: "CANCEL",
        }}
        onConfirm={handleOpenReviewForm}  // → open Step 2
        onCancel={handleCancelFirstConfirm}
      />

      {/* 2) Leave a review? (Yes / No) — uses SAME ConfirmationModal UI */}
      {/*<ConfirmationModal*/}
      {/*  isOpen={isReviewPromptOpen}*/}
      {/*  options={{*/}
      {/*    type: "delete",*/}
      {/*    title: "Leave a review?",*/}
      {/*    message: "Do you want to leave a rating & short review before removing this tenant?",*/}
      {/*    confirmText: "Yes",*/}
      {/*    cancelText: "No"*/}
      {/*  }}*/}
      {/*  onConfirm={handleOpenReviewForm}      // → open Step 3 (Rate & Review)*/}
      {/*  onCancel={handleDeleteWithoutReview} // → delete immediately*/}
      {/*/>*/}

      {/* 3) Rate & Review Modal */}
      <RateReviewModal
        isOpen={isReviewFormOpen}
        rating={reviewRating}
        comment={reviewComment}
        onChangeRating={(val) => setReviewRating(val)}
        onChangeComment={(val) => setReviewComment(val)}
        onCancel={() => setIsReviewFormOpen(false)}
        onSubmit={handleSubmitReviewAndDelete}
        loading={isSubmittingReview || isDeletingTenant}
      />

      {/* Previous Ratings Modal */}
      <PreviousRatingsModal
        isOpen={isPrevRatingsOpen}
        onClose={() => setIsPrevRatingsOpen(false)}
        tenantEmail={formData.email}
        tenantPhone={formData.phone}
        ratings={prevRatings}
        loading={isLoadingPrevRatings}
      />
    </div>
  );
};

export default Tenantdetailsform;
